import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import {
  TextField,
  FormControl,
  InputLabel,
  Box,
  IconButton
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import ClearIcon from '@mui/icons-material/Clear'
import dayjs from 'dayjs'

interface CustomDateTimePickerProps {
  dateType?: 'date' | 'datetime'
  name: string
  label?: string
  value?: string
  onChange?: (e: { target: { value: string; name: string } }) => void
  required?: boolean
  extraProps?: any
}

const CustomDateTimePicker = React.memo(
  ({
    dateType = 'date',
    name,
    label,
    value: propValue,
    onChange,
    required,
    extraProps
  }: CustomDateTimePickerProps) => {
    const hiddenInputRef = useRef<HTMLInputElement>(null)
    const [internalValue, setInternalValue] = useState<string | undefined>(
      propValue
    )

    useEffect(() => {
      if (propValue !== internalValue) {
        setInternalValue(propValue)
      }
    }, [propValue])

    const handleDateChange = useCallback(
      (newValue: dayjs.Dayjs | null) => {
        const formattedValue = newValue
          ? dateType === 'date'
            ? newValue.format('YYYY-MM-DD')
            : newValue.format('YYYY-MM-DD HH:mm')
          : ''

        setInternalValue(formattedValue)

        if (hiddenInputRef.current) {
          hiddenInputRef.current.value = formattedValue
        }

        onChange?.({ target: { value: formattedValue, name } })
      },
      [dateType, name, onChange]
    )

    const handleClear = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation()
        handleDateChange(null)
      },
      [handleDateChange]
    )

    const Picker = useMemo(
      () => (dateType === 'date' ? DatePicker : DateTimePicker),
      [dateType]
    )

    const renderTextField = useCallback(
      (params: any) => (
        <TextField
          {...params}
          variant='standard'
          InputProps={{
            ...params.InputProps,
            disableUnderline: true
          }}
          sx={{
            width: '100%'
          }}
        />
      ),
      []
    )

    return (
      <>
        <FormControl
          variant='outlined'
          fullWidth
          required={required}
          sx={{
            '& .MuiFormLabel-root': {
              backgroundColor: 'white',
              px: 1,
              '&.Mui-focused': {
                color: 'primary.main'
              }
            }
          }}>
          <InputLabel shrink>{label}</InputLabel>
          <Box
            sx={{
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: 1,
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.87)'
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.5,
              minHeight: '36px',
              height: '36px',
              position: 'relative',
              ...(extraProps?.sx ?? {})
            }}>
            <Picker
              value={internalValue ? dayjs(internalValue) : null}
              onChange={handleDateChange}
              format={dateType === 'date' ? 'YYYY/MM/DD' : 'YYYY/MM/DD HH:mm'}
              closeOnSelect={true}
              slots={{
                textField: renderTextField
              }}
              slotProps={{
                popper: {
                  sx: {
                    '& .MuiPickersYear-yearButton': {
                      fontSize: '14px'
                    },
                    '& .MuiMultiSectionDigitalClock-root': {
                      '& .MuiMenuItem-root': {
                        fontSize: '14px',
                        padding: '4px 8px'
                      }
                    },
                    '& .MuiDialogActions-root': {
                      display: 'none'
                    }
                  }
                },

                actionBar: {
                  actions: []
                }
              }}
            />

            {internalValue && (
              <IconButton
                size='small'
                sx={{
                  position: 'absolute',
                  right: '35px',
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  padding: 0.5,
                  ':hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  },
                  '.MuiBox-root:hover &': {
                    opacity: 1
                  }
                }}
                onClick={handleClear}>
                <ClearIcon fontSize='small' />
              </IconButton>
            )}
          </Box>
        </FormControl>

        <input
          type='hidden'
          ref={hiddenInputRef}
          name={name}
          value={internalValue || ''}
          onChange={() => {}}
        />
      </>
    )
  }
)

export default CustomDateTimePicker
